import clsx from "clsx";
import { cloneElement, forwardRef, isValidElement } from "react";
import type { ComponentRef, ReactElement, ReactNode } from "react";
import { buttonVariants } from "./variants";
import ButtonIcon from "./ButtonIcon";
import Spinner from "./Spinner";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "fill" | "outline" | "ghost";
  size?: "small" | "medium" | "large";
  color?: "primary" | "primary-low" | "secondary" | "danger" | "info";
  prefixIcon?: ReactNode;
  suffixIcon?: ReactNode;
  isLoading?: boolean;
  asChild?: boolean;
  className?: string;
}

const gapClassMap: Record<NonNullable<ButtonProps["size"]>, string> = {
  small: "gap-x-1",
  medium: "gap-x-[6px]",
  large: "gap-x-2",
};

export const Button = forwardRef<ComponentRef<"button">, ButtonProps>((componentProps, ref) => {
  const {
    variant = "fill",
    size = "medium",
    color = "primary",
    disabled = false,
    type = "button",
    isLoading = false,
    prefixIcon: PrefixIcon,
    suffixIcon: SuffixIcon,
    asChild = false,
    className,
    children,
    ...rest
  } = componentProps;

  const shouldDelegateToChild = asChild && isValidElement(children);

  const actualButtonContent = shouldDelegateToChild
    ? (children as ReactElement<{ children?: ReactNode }>).props.children
    : children;

  const buttonInnerUi = (
    <>
      {isLoading && (
        <span className="flex absolute inset-0 justify-center items-center" aria-hidden="true">
          <Spinner />
        </span>
      )}
      <span className={clsx("inline-flex items-center justify-center", gapClassMap[size], isLoading && "invisible")}>
        {<ButtonIcon icon={PrefixIcon} size={size} />}
        {actualButtonContent != null && actualButtonContent !== "" && (
          <span className="text-inherit">{actualButtonContent}</span>
        )}
        {<ButtonIcon icon={SuffixIcon} size={size} />}
      </span>
      {isLoading && <span className="sr-only">Loading...</span>}
    </>
  );

  const buttonProps = {
    className: clsx(
      buttonVariants({
        variant,
        size,
        color,
        hasPrefixIcon: !!PrefixIcon,
        hasSuffixIcon: !!SuffixIcon,
      }),
      "relative inline-flex items-center justify-center",
      className,
    ),
    disabled: disabled || isLoading,
    "aria-disabled": disabled || isLoading,
    "aria-busy": isLoading,
    tabIndex: disabled || isLoading ? -1 : undefined,
    ref: ref,
    ...rest,
  };

  if (shouldDelegateToChild) {
    const childElement = children as ReactElement<{ className?: string; children?: ReactNode }>;

    return cloneElement(
      childElement,
      {
        ...childElement.props,
        ...buttonProps,
        className: clsx(buttonProps.className, childElement.props.className),
      },
      buttonInnerUi,
    );
  }

  return (
    <button type={type} {...buttonProps}>
      {buttonInnerUi}
    </button>
  );
});

Button.displayName = "Button";
